/**
 * Pure helpers for payment-agreement installment calculations.
 * Framework-free so they can be unit-tested with Vitest.
 *
 * Shapes:
 *   installment = { id, agreement_id, amount, status }
 *   allocation  = { installment_id, amount }
 *   agreement   = { id, status }  // 'Active' | 'Completed' | 'Cancelled'
 */

/** Sum of installment amounts; missing amounts count as 0. */
export function sumInstallments(installments) {
  return (installments ?? []).reduce((s, i) => s + (i?.amount ?? 0), 0)
}

/** Total amount allocated (paid) toward a specific installment id. */
export function installmentPaidAmount(installmentId, allocations) {
  return (allocations ?? [])
    .filter(a => a?.installment_id === installmentId)
    .reduce((s, a) => s + (a?.amount ?? 0), 0)
}

/** Remaining amount owed on an installment, floored at 0. */
export function installmentRemaining(installment, allocations) {
  const paid = installmentPaidAmount(installment?.id, allocations)
  return Math.max(0, (installment?.amount ?? 0) - paid)
}

/** 'Paid' when allocations cover the amount (within a cent), else 'Unpaid'. */
export function deriveInstallmentStatus(installment, allocations) {
  const paid = installmentPaidAmount(installment?.id, allocations)
  return paid + 0.005 >= (installment?.amount ?? 0) ? 'Paid' : 'Unpaid'
}

/** 'Completed' when all installments are Paid, else 'Active'. */
export function deriveAgreementStatus(installments, allocations) {
  const list = installments ?? []
  const allPaid = list.length > 0 &&
    list.every(i => deriveInstallmentStatus(i, allocations) === 'Paid')
  return allPaid ? 'Completed' : 'Active'
}

/**
 * Sum of remaining amounts for Unpaid installments whose agreement is Active.
 */
export function pendingAgreementIncome(agreements, installments, allocations) {
  const activeIds = new Set(
    (agreements ?? []).filter(a => a?.status === 'Active').map(a => a.id),
  )
  return (installments ?? [])
    .filter(i => activeIds.has(i?.agreement_id))
    .filter(i => deriveInstallmentStatus(i, allocations) === 'Unpaid')
    .reduce((s, i) => s + installmentRemaining(i, allocations), 0)
}
